import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { productTourSteps } from './productTourSteps';

const EASE_REFINED = [0.22, 1, 0.36, 1];

function StepPreview({ stepId }) {
  if (stepId === '01') {
    return (
      <div
        style={{
          marginTop: '1.125rem',
          padding: '14px 16px',
          borderRadius: 10,
          background: 'var(--bg-s1)',
          border: '1px dashed var(--border-strong)',
        }}
      >
        <div
          style={{
            fontFamily: 'Inter, DM Sans, sans-serif',
            fontSize: '0.6875rem',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
            marginBottom: 10,
            fontWeight: 500,
          }}
        >
          Drop CSV or resume
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gap: 8,
            padding: '10px 12px',
            borderRadius: 8,
            background: 'var(--bg-card)',
            border: '1px solid var(--border-main)',
          }}
        >
          <div>
            <div style={{ fontFamily: 'DM Sans, Inter, sans-serif', fontSize: '0.8125rem', color: 'var(--text-primary)', fontWeight: 500 }}>
              Sarah Chen
            </div>
            <div style={{ fontFamily: 'DM Sans, Inter, sans-serif', fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 2 }}>
              Product Lead · $145K
            </div>
          </div>
          <span
            style={{
              alignSelf: 'center',
              fontFamily: 'Inter, DM Sans, sans-serif',
              fontSize: '0.625rem',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: '#2D7A4A',
              background: 'rgba(45,122,74,0.08)',
              border: '1px solid rgba(45,122,74,0.18)',
              padding: '3px 8px',
              borderRadius: 999,
              fontWeight: 600,
            }}
          >
            Added
          </span>
        </div>
      </div>
    );
  }

  if (stepId === '02') {
    return (
      <div
        style={{
          marginTop: '1.125rem',
          padding: '14px 16px',
          borderRadius: 10,
          background: 'var(--bg-s1)',
          border: '1px solid var(--border-main)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
          <div>
            <div style={{ fontFamily: 'DM Sans, Inter, sans-serif', fontSize: '0.8125rem', color: 'var(--text-primary)', fontWeight: 500 }}>
              Christopher Williams
            </div>
            <div style={{ fontFamily: 'DM Sans, Inter, sans-serif', fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 2 }}>
              Senior Engineer
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '1.375rem', fontWeight: 700, color: 'var(--risk-high-fg, #C2410C)', lineHeight: 1 }}>
              83
            </div>
            <div
              style={{
                marginTop: 4,
                fontFamily: 'Inter, DM Sans, sans-serif',
                fontSize: '0.5625rem',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'var(--risk-high-fg, #C2410C)',
                fontWeight: 600,
              }}
            >
              High risk
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {['Pay vs market', 'Replacement cost', 'Role demand'].map((label) => (
            <span
              key={label}
              style={{
                fontFamily: 'DM Sans, Inter, sans-serif',
                fontSize: '0.6875rem',
                color: 'var(--text-secondary)',
                background: 'var(--bg-card)',
                border: '1px solid var(--border-subtle)',
                padding: '4px 8px',
                borderRadius: 6,
              }}
            >
              {label}
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: '1.125rem',
        padding: '14px 16px',
        borderRadius: 10,
        background: 'var(--bg-s1)',
        border: '1px solid var(--border-main)',
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 14 }}>
        {[
          { label: 'At risk', value: '12' },
          { label: 'In progress', value: '4' },
          { label: 'Avg risk', value: '64' },
        ].map(({ label, value }) => (
          <div key={label} style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '1.125rem', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1 }}>
              {value}
            </div>
            <div style={{ fontFamily: 'DM Sans, Inter, sans-serif', fontSize: '0.625rem', color: 'var(--text-muted)', marginTop: 4 }}>
              {label}
            </div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 36 }}>
        {[28, 42, 35, 58, 52, 44, 38].map((h, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: `${h}%`,
              borderRadius: 3,
              background: i === 3 ? 'var(--accent-blue)' : 'var(--border-strong)',
              opacity: i === 3 ? 1 : 0.55,
            }}
          />
        ))}
      </div>
    </div>
  );
}

function MobileStepCard({ step, active, delay }) {
  const { Icon } = step;

  return (
    <motion.article
      initial={{ opacity: 0, y: 18 }}
      animate={active ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, ease: EASE_REFINED, delay }}
      style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border-main)',
        borderRadius: 16,
        padding: '1.25rem 1.125rem 1.125rem',
        boxShadow: '0 8px 28px var(--shadow-card)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
        <Icon
          className="app-inline-icon app-inline-icon--md shrink-0 mt-0.5"
          style={{ color: 'var(--accent-blue)' }}
          strokeWidth={1.75}
          aria-hidden
        />
        <div style={{ minWidth: 0, flex: 1 }}>
          <span
            aria-hidden="true"
            style={{
              display: 'block',
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.625rem',
              letterSpacing: '0.08em',
              color: 'var(--accent-blue)',
              fontWeight: 600,
              marginBottom: 3,
            }}
          >
            {step.number}
          </span>
          <h3
            style={{
              fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
              fontWeight: 600,
              fontSize: '1rem',
              lineHeight: 1.25,
              color: 'var(--text-primary)',
              letterSpacing: '-0.015em',
            }}
          >
            {step.mobileTitle}
          </h3>
        </div>
      </div>

      <p
        style={{
          fontFamily: 'DM Sans, Inter, sans-serif',
          fontWeight: 400,
          fontSize: '0.8125rem',
          lineHeight: 1.5,
          color: 'var(--text-secondary)',
        }}
      >
        {step.mobileDescription}
      </p>

      <StepPreview stepId={step.number} />
    </motion.article>
  );
}

export default function ProductTourMobile({ embedded = false }) {
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, margin: '-8%' });

  return (
    <section
      ref={sectionRef}
      id="product"
      className="product-tour-mobile"
      style={{
        padding: 'clamp(72px, 14vw, 96px) 0 clamp(56px, 10vw, 72px)',
        background: embedded ? 'transparent' : 'var(--bg-s1)',
        borderTop: embedded ? 'none' : '1px solid var(--border-divider)',
      }}
    >
      <div className="max-w-7xl mx-auto px-6 w-full">
        <motion.h2
          className="landing-heading"
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: EASE_REFINED, delay: 0.05 }}
          style={{
            fontSize: 'clamp(1.75rem, 7vw, 2.25rem)',
            lineHeight: 1.1,
            color: 'var(--text-primary)',
            letterSpacing: '-0.025em',
            marginBottom: '1.25rem',
          }}
        >
          See Stavyn{' '}
          <span className="headline-accent">in action.</span>
        </motion.h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {productTourSteps.map((step, i) => (
            <MobileStepCard key={step.number} step={step} active={inView} delay={0.18 + i * 0.08} />
          ))}
        </div>
      </div>
    </section>
  );
}
